'use client';

import React, { useMemo } from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';
import Link from 'next/link';
import { exists } from 'src/utils/exists/exists';
import { formatPhoneNumber } from 'src/utils/formatPhoneNumber/formatPhoneNumber';
import { createGoogleMapsUrl } from 'src/utils/createGoogleMapsLink';
import { Box, ProfileCardHeader, Card, Typography } from 'src/components/PulseUI';
import { SOC_TYPE_LABELS, formatSocClassification, titleCaseId } from './socTypes';

export { SOC_TYPE_LABELS, formatSocClassification };

const traumaLabelMap = {
  1: 'Level I',
  2: 'Level II',
  3: 'Level III',
  4: 'Level IV',
  5: 'Level V',
};

const additionalDetailsMap = {
  alos_days: 'Average Length of Stay',
  operating_room_count: 'Operating Rooms',
  total_bed_count: 'Staffed Beds',
  mac_carrier: 'Medicare Administrative Contractor (MAC)',
  adult_trauma_level: 'Adult Trauma',
  pediatric_trauma_level: 'Pediatric Trauma',
};


const orderedDetailTitles = [
  'Type',
  'Subtype',
  'Specialty',
  'Name',
  'Address',
  'City/State',
  'Phone',
  'Website',
  'Average Length of Stay',
  'Operating Rooms',
  'Staffed Beds',
  'Medicare Administrative Contractor (MAC)',
  'Adult Trauma',
  'Pediatric Trauma',
];

const ProfileSiteOfCareDetails = () => {
  const { entityDetails } = useProfile();

  const details = useMemo(() => {
    const detailsMap = new Map<string, string | null>(
      orderedDetailTitles.map((title) => [title, null])
    );

    if (entityDetails?.soc_type) {
      detailsMap.set(
        'Type',
        SOC_TYPE_LABELS[entityDetails.soc_type] ?? titleCaseId(entityDetails.soc_type)
      );
    }

    if (entityDetails?.soc_subtype) {
      detailsMap.set('Subtype', entityDetails.soc_subtype);
    }

    if (entityDetails?.specialty) {
      detailsMap.set('Specialty', entityDetails.specialty);
    }

    if (entityDetails?.name) {
      detailsMap.set('Name', entityDetails.name);
    }

    if (entityDetails?.website) {
      detailsMap.set('Website', entityDetails.website);
    }

    if (entityDetails?.phone) {
      const formattedPhone = formatPhoneNumber(entityDetails.phone);
      if (formattedPhone) {
        detailsMap.set('Phone', formattedPhone);
      }
    }

    const primaryAddress = entityDetails?.primary_address;
    if (primaryAddress) {
      const addressValue = [
        primaryAddress.address_line_1,
        primaryAddress.address_line_2,
      ]
        .filter(Boolean)
        .join(' ');
      if (addressValue) {
        detailsMap.set('Address', addressValue);
      }

      const city = primaryAddress.city;
      const state = primaryAddress.state;
      if (city && state) {
        detailsMap.set('City/State', `${city}, ${state}`);
      }
    }

    Object.entries(additionalDetailsMap).forEach(([key, title]) => {
      if (detailsMap.get(title) !== null) return;

      const rawValue = entityDetails?.[key];

      if (!exists(rawValue) || rawValue === '') return;

      const formatters = {
        adult_trauma_level: (val) => traumaLabelMap[val] ?? null,
        pediatric_trauma_level: (val) => traumaLabelMap[val] ?? null,
        alos_days: (val) => `${parseFloat(val)?.toFixed(1)} days`,
        operating_room_count: (val) => String(val),
        total_bed_count: (val) => String(val),
      };

      const processedValue = formatters[key]?.(rawValue) ?? rawValue;

      if (processedValue !== null) {
        detailsMap.set(title, processedValue);
      }
    });

    return orderedDetailTitles.map((title) => ({
      title,
      value: detailsMap.get(title),
    }));
  }, [entityDetails]);

  return (
    <Card className="p-6" data-testid="soc-details">
      <ProfileCardHeader
        icon={entityDetails?.soc_type || 'site_of_care'}
        title="Details"
        infoTooltip={false}
      />
      <Box className="flex flex-col gap-2">
        {details.map((item) => {
          const isAddress = item.title === 'Address';
          const isWebsite = item.title === 'Website';
          const mapsUrl =
            isAddress && item.value
              ? createGoogleMapsUrl(entityDetails?.primary_address ?? {})
              : '';

          return (
            <Box key={item.title} className="flex flex-col" data-testid={`soc-detail-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <Typography variant="body2" color="tertiary">
                {item.title}
              </Typography>
              <Typography variant="body2" color="primary" bold>
                {isWebsite && typeof item.value === 'string' && item.value ? (
                  <Link
                    href={
                      item.value.startsWith('http')
                        ? item.value
                        : `http://${item.value}`
                    }
                    scroll={false}
                    className="text-link block overflow-hidden text-ellipsis whitespace-nowrap max-w-full"
                  >
                    {item.value}
                  </Link>
                ) : isAddress && item.value ? (
                  <Link
                    href={mapsUrl}
                    scroll={false}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-link"
                    aria-label={`View ${item.value} on Google Maps`}
                  >
                    {item.value}
                  </Link>
                ) : (
                  item.value || 'Not available'
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

export default ProfileSiteOfCareDetails;
